import React, { useEffect, useRef, useState } from "react";
import { DesktopIcon, MobileIcon } from "../Auth/onboarding/icons";
import { LaptopIcon, PhoneFrameIcon, HamburgerIcon } from "./icons";
import WebsiteMock from "./WebsiteMock";
import WidgetPreview, { launcherIcon } from "./WidgetPreview";
import { screenshotSrc } from "../../utils/screenshot";
import {
  alignClass,
  isCustomPosition,
  offsetFromPointer,
  placementStyles,
} from "./widgetPlacement";

export default function PreviewPanel({ settings, siteUrl = "", onChange }) {
  const [device, setDevice] = useState("desktop");
  const [dragging, setDragging] = useState(false);
  const stageRef = useRef(null);
  // "failed" drops back to the mock page, so a site the renderer can't capture
  // still leaves a usable backdrop behind the widget.
  const [shotState, setShotState] = useState("idle");
  const positionClass = alignClass(settings.widgetPosition);

  // A custom placement is set by dragging the widget around the stage, which
  // only works if the page gave us a way to write it back.
  const draggable =
    isCustomPosition(settings.widgetPosition) && typeof onChange === "function";

  const { panel: panelStyle, launcher: launcherStyle } = placementStyles(
    settings.widgetPosition,
    { x: settings.widgetOffsetX, y: settings.widgetOffsetY },
  );

  // Measured against the stage, not the widget, so what gets stored is where
  // the pointer is over the "page" - the same share of the screen the live
  // widget reads it back as.
  const placeAtPointer = (event) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const next = offsetFromPointer(event, rect);
    onChange("widgetOffsetX", next.x);
    onChange("widgetOffsetY", next.y);
  };

  const dragHandlers = draggable
    ? {
        onPointerDown: (event) => {
          // Capture, so a drag that outruns the cursor keeps feeding this
          // element instead of dropping the widget mid-flight.
          event.currentTarget.setPointerCapture?.(event.pointerId);
          event.preventDefault();
          setDragging(true);
          placeAtPointer(event);
        },
        onPointerMove: (event) => {
          if (dragging) placeAtPointer(event);
        },
        onPointerUp: (event) => {
          event.currentTarget.releasePointerCapture?.(event.pointerId);
          setDragging(false);
        },
        onPointerCancel: () => setDragging(false),
      }
    : null;

  const shot = screenshotSrc(siteUrl);

  // A different site deserves a fresh attempt, even if the last one failed.
  useEffect(() => {
    setShotState(shot ? "loading" : "idle");
  }, [shot]);

  const showShot = Boolean(shot) && shotState !== "failed";

  // The renderer can take 20-30s on a slow site, so the mock stays underneath
  // as the backdrop the whole time and the real screenshot fades in on top
  // once it arrives. Swapping one for the other left a blank grey box for the
  // entire wait, which read as broken.
  const backdrop = (
    <div className="cz-site-backdrop">
      <WebsiteMock accentColor={settings.accentColor} />

      {showShot && (
        <img
          className={"cz-site-shot ready"}
          // className={`cz-site-shot${shotState === "ready" ? " is-ready" : ""}`}
          src={shot}
          alt=""
          referrerPolicy="no-referrer"
          onLoad={() => setShotState("ready")}
          onError={() => setShotState("failed")}
        />
      )}

      {/* {showShot && shotState !== "ready" && (
        <span className="cz-site-shot-badge">
          <span className="install-status-spinner" aria-hidden="true" />
          Loading your site&hellip;
        </span>
      )} */}
    </div>
  );

  return (
    <div className="cz-preview-col">
      <section className="dash-card cz-preview-card">
        <div className="dash-card-header">
          <span className="cz-preview-title">
            <LaptopIcon />
            {device === "desktop" ? "Desktop preview" : "Mobile preview"}
          </span> 
          <div className="cz-device-toggle">
            <button
              type="button"
              className={device === "desktop" ? "is-active" : ""}
              onClick={() => setDevice("desktop")}
              aria-label="Desktop preview"
            >
              <DesktopIcon />
            </button>
            <button
              type="button"
              className={device === "mobile" ? "is-active" : ""}
              onClick={() => setDevice("mobile")}
              aria-label="Mobile preview"
            >
              <MobileIcon />
            </button>
          </div>
        </div>

        {device === "desktop" ? (
          <div
            ref={stageRef}
            className={`cz-preview-stage makeHeight ${positionClass}${
              draggable ? " is-draggable" : ""
            }${dragging ? " is-dragging" : ""}`}
          >
            {backdrop}
            <div
              className="cz-floating-widget"
              style={panelStyle}
              {...dragHandlers}
            >
              <WidgetPreview settings={settings} />
            </div>
            <span
              className="cz-launcher-btn"
              style={{
                ...launcherStyle,
                ...(settings.avatarImage
                  ? null
                  : { background: settings.buttonColor }),
              }}
              {...dragHandlers}
            >
              {launcherIcon(settings)}
            </span>
          </div>
        ) : (
          <MobileFrame settings={settings} />
        )}
      </section>

      {/* <section className="dash-card cz-preview-card">
        <div className="dash-card-header">
          <span className="cz-preview-title">
            <PhoneFrameIcon />
            Mobile preview
          </span>
        </div>
        <MobileFrame settings={settings} />
      </section> */}
    </div>
  );
}

function MobileFrame({ settings }) {
  return (
    <div className="cz-phone-frame">
      <div className="cz-phone-header">
        <span className="cz-site-logo">ACME</span>
        <HamburgerIcon />
      </div>
      <div className="cz-phone-body">
        <WidgetPreview settings={settings} docked />
      </div>
      <div className="cz-phone-home-bar" />
    </div>
  );
}
